import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout, { adminFetch } from './AdminLayout';

const RESOURCE_TYPES = ['', 'client', 'dossier', 'matching', 'catalog'];

export default function AdminAudit() {
  const [filters, setFilters] = useState({
    agentId: '',
    clientId: '',
    resourceType: '',
    from: '',  // ISO date
    to: '',
  });
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const buildQuery = (cursor) => {
    const params = new URLSearchParams();
    if (filters.agentId) params.set('agentId', filters.agentId);
    if (filters.clientId) params.set('clientId', filters.clientId);
    if (filters.resourceType) params.set('resourceType', filters.resourceType);
    if (filters.from) params.set('from', new Date(filters.from).toISOString());
    if (filters.to) params.set('to', new Date(filters.to).toISOString());
    if (cursor) params.set('cursor', cursor);
    params.set('limit', '50');
    return params.toString();
  };

  const fetchPage = useCallback(async (cursor) => {
    setLoading(true); setError(null);
    try {
      const res = await adminFetch(`/api/admin/audit/agent-access?${buildQuery(cursor)}`);
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const data = await res.json();
      if (cursor) {
        setItems(prev => [...prev, ...data.items]);
      } else {
        setItems(data.items);
        setTotal(data.total ?? data.items.length);
      }
      setNextCursor(data.nextCursor);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load initial sur mount
  useEffect(() => { fetchPage(null); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = () => fetchPage(null);
  const loadMore = () => nextCursor && fetchPage(nextCursor);

  const formatDate = (iso) => new Date(iso).toLocaleString('fr-FR', {
    dateStyle: 'short', timeStyle: 'medium',
  });

  return (
    <AdminLayout title="Audit accès agents">
      <div style={{ padding: 24 }}>
        <h2 style={{ color: '#5A3C1F', marginBottom: 16 }}>Audit accès agents</h2>

        {/* Filtres */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
          marginBottom: 16,
          padding: 16,
          background: '#FFF8F0',
          borderRadius: 8,
          border: '1px solid #E0DDD8',
        }}>
          <label style={{ fontSize: 12, color: '#5A3C1F' }}>
            Agent ID
            <input
              value={filters.agentId}
              onChange={e => setFilters(f => ({ ...f, agentId: e.target.value }))}
              placeholder="ex: agent-1"
              style={inputStyle}
            />
          </label>
          <label style={{ fontSize: 12, color: '#5A3C1F' }}>
            Client ID
            <input
              value={filters.clientId}
              onChange={e => setFilters(f => ({ ...f, clientId: e.target.value }))}
              style={inputStyle}
            />
          </label>
          <label style={{ fontSize: 12, color: '#5A3C1F' }}>
            Type ressource
            <select
              value={filters.resourceType}
              onChange={e => setFilters(f => ({ ...f, resourceType: e.target.value }))}
              style={inputStyle}
            >
              {RESOURCE_TYPES.map(rt => (
                <option key={rt} value={rt}>{rt || 'Tous'}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#5A3C1F' }}>
            Du
            <input
              type="date"
              value={filters.from}
              onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
              style={inputStyle}
            />
          </label>
          <label style={{ fontSize: 12, color: '#5A3C1F' }}>
            Au
            <input
              type="date"
              value={filters.to}
              onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
              style={inputStyle}
            />
          </label>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button onClick={applyFilters} disabled={loading} style={btnStyle}>
              {loading ? 'Chargement…' : 'Appliquer'}
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: 12, background: '#FEE', color: '#933', borderRadius: 8, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: 8, color: '#6B5847', fontSize: 14 }}>
          {items.length} sur {total} entrées
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 8, border: '1px solid #E0DDD8' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F5EFE6' }}>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Agent</th>
                <th style={thStyle}>Action</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Resource ID</th>
                <th style={thStyle}>Client ID</th>
                <th style={thStyle}>Metadata</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#6B5847' }}>
                  Aucune entrée trouvée
                </td></tr>
              )}
              {items.map(item => (
                <tr key={item.id} style={{ borderTop: '1px solid #E0DDD8' }}>
                  <td style={tdStyle}>{formatDate(item.occurredAt)}</td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600 }}>{item.agentEmail || item.agentId}</div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: 4,
                      background: actionColor(item.action),
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 600,
                    }}>{item.action}</span>
                  </td>
                  <td style={tdStyle}>{item.resourceType}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.resourceId}
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>
                    {item.clientId || '—'}
                  </td>
                  <td style={tdStyle}>
                    {item.metadata && Object.keys(item.metadata).length > 0 ? (
                      <details>
                        <summary style={{ cursor: 'pointer', color: '#A0763E', fontSize: 11 }}>Voir</summary>
                        <pre style={{ fontSize: 10, margin: 4, padding: 4, background: '#F5EFE6', borderRadius: 4 }}>
                          {JSON.stringify(item.metadata, null, 2)}
                        </pre>
                      </details>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {nextCursor && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button onClick={loadMore} disabled={loading} style={btnStyle}>
              {loading ? 'Chargement…' : 'Charger plus'}
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

// Inline styles
const inputStyle = {
  width: '100%',
  marginTop: 4,
  padding: '8px 10px',
  border: '1px solid #D1CCC2',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: 'inherit',
};

const btnStyle = {
  padding: '10px 24px',
  background: '#A0763E',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
};

const thStyle = {
  padding: '12px 8px',
  textAlign: 'left',
  fontWeight: 600,
  color: '#5A3C1F',
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const tdStyle = {
  padding: '10px 8px',
  color: '#5A3C1F',
  verticalAlign: 'top',
};

function actionColor(action) {
  switch (action) {
    case 'view': return '#7A9E6E';         // vert
    case 'edit': return '#A0763E';         // ocre
    case 'delete': return '#C03434';       // rouge
    case 'run_matching': return '#5A85B8'; // bleu
    case 'create_dossier': return '#9B7BA0'; // violet
    default: return '#888';
  }
}
